import { identityFetch } from './identityAuth';
import type {
  FacilityCreatePayload,
  FacilityRecord,
  FacilityUpdatePayload,
  InviteUserPayload,
  MeUser,
  RoleCreatePayload,
  RoleRecord,
  RoleUpdatePayload,
  UserRecord,
  UserUpdatePayload,
} from '../types/registry';

export async function fetchMe(): Promise<MeUser> {
  const data = await identityFetch<{ ok: boolean; user: MeUser }>('/me');
  return data.user;
}

export async function fetchUsers(): Promise<UserRecord[]> {
  const data = await identityFetch<{ ok: boolean; users: UserRecord[] }>('/users');
  return data.users;
}

export async function fetchUser(userId: string): Promise<UserRecord> {
  const data = await identityFetch<{ ok: boolean; user: UserRecord }>(`/users/${userId}`);
  return data.user;
}

export async function updateUser(userId: string, payload: UserUpdatePayload): Promise<UserRecord> {
  const data = await identityFetch<{ ok: boolean; user: UserRecord }>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return data.user;
}

export async function inviteUser(payload: InviteUserPayload) {
  return identityFetch<{
    ok: boolean;
    user_id: string;
    role: string;
    assigned_instances: InviteUserPayload['assigned_instances'];
    assigned_spaces: string[];
  }>('/users/invite', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function disableUser(userId: string) {
  return identityFetch<{ ok: boolean; user_id: string; status: string }>(
    `/users/${userId}/disable`,
    { method: 'POST', body: '{}' },
  );
}

export async function enableUser(userId: string) {
  return identityFetch<{ ok: boolean; user_id: string; status: string }>(
    `/users/${userId}/enable`,
    { method: 'POST', body: '{}' },
  );
}

export async function deleteUser(userId: string) {
  return identityFetch<{ ok: boolean; user_id: string; status: string }>(
    `/users/${userId}`,
    { method: 'DELETE' },
  );
}

export async function fetchRoles(): Promise<RoleRecord[]> {
  const data = await identityFetch<{ ok: boolean; roles: RoleRecord[] }>('/roles');
  return data.roles;
}

export async function createRole(payload: RoleCreatePayload): Promise<RoleRecord> {
  const data = await identityFetch<{ ok: boolean; role: RoleRecord }>('/roles', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return data.role;
}

export async function updateRole(roleId: string, payload: RoleUpdatePayload): Promise<RoleRecord> {
  const data = await identityFetch<{ ok: boolean; role: RoleRecord }>(`/roles/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return data.role;
}

export async function deleteRole(roleId: string) {
  return identityFetch<{ ok: boolean; role_id: string }>(`/roles/${roleId}`, {
    method: 'DELETE',
  });
}

export async function fetchFacilities(): Promise<FacilityRecord[]> {
  const data = await identityFetch<{ ok: boolean; facilities: FacilityRecord[] }>('/facilities');
  return data.facilities;
}

export async function getFacility(facilityId: string): Promise<FacilityRecord> {
  const data = await identityFetch<{ ok: boolean; facility: FacilityRecord }>(
    `/facilities/${facilityId}`,
  );
  return data.facility;
}

export async function createFacility(payload: FacilityCreatePayload): Promise<FacilityRecord> {
  const data = await identityFetch<{ ok: boolean; facility: FacilityRecord }>('/facilities', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return data.facility;
}

export async function updateFacility(
  facilityId: string,
  payload: FacilityUpdatePayload,
): Promise<FacilityRecord> {
  const data = await identityFetch<{ ok: boolean; facility: FacilityRecord }>(
    `/facilities/${facilityId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
  return data.facility;
}

export async function deleteFacility(facilityId: string) {
  return identityFetch<{ ok: boolean; facility_id: string; status: string }>(
    `/facilities/${facilityId}`,
    { method: 'DELETE' },
  );
}

export interface AssignablePermissions {
  permissions: string[];
  client_type: string;
  source_template_role_id: string | null;
}

export async function fetchPermissions(): Promise<AssignablePermissions> {
  const data = await identityFetch<{
    ok: boolean;
    client_type: string;
    source_template_role_id: string | null;
    permissions: string[];
  }>('/permissions');
  return {
    permissions: data.permissions ?? [],
    client_type: data.client_type,
    source_template_role_id: data.source_template_role_id ?? null,
  };
}
